import { z } from "zod";
import { enUS, ptBR } from "date-fns/locale";
import type { Locale } from "date-fns";

export const DateLocale = {
	EnUS: "en-US",
	PtBR: "pt-BR",
} as const;

export const dateLocaleSchema = z.enum(DateLocale);
export type DateLocale = z.infer<typeof dateLocaleSchema>;

/**
 * Retorna a locale padronizada BCP 47 ("en-US" | "pt-BR") a partir de uma string de idioma ou instância do i18n.
 *
 * @example
 * getDateLocale(i18n.language) // "en-US" ou "pt-BR"
 * getDateLocale(i18n)          // aceita o próprio objeto i18n
 * getDateLocale("en")          // "en-US"
 * getDateLocale()              // "pt-BR" (fallback padrão)
 */
export function getDateLocale(lang?: string | { language?: string } | null): DateLocale {
	const code = typeof lang === "string" ? lang : lang?.language;
	if (!code) return DateLocale.PtBR;
	return code.toLowerCase().startsWith("en") ? DateLocale.EnUS : DateLocale.PtBR;
}

/**
 * Retorna a locale do date-fns (enUS | ptBR) correspondente ao idioma informado.
 *
 * @example
 * format(date, "dd/MM/yyyy", { locale: getDateFnsLocale(i18n.language) })
 * format(date, "dd/MM/yyyy", { locale: getDateFnsLocale(i18n) })
 */
export function getDateFnsLocale(lang?: string | { language?: string } | null): Locale {
	return getDateLocale(lang) === DateLocale.EnUS ? enUS : ptBR;
}

/**
 * Retorna true se a locale atual for inglês.
 */
export function isEnLocale(lang?: string | { language?: string } | null): boolean {
	return getDateLocale(lang) === DateLocale.EnUS;
}

/**
 * Iniciais do nome para avatares (ex: "Roberto Souza" -> "RS", "Maria" -> "MA").
 */
export function getInitials(name?: string | null, fallback = "U"): string {
	if (!name) return fallback;
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
	}
	return name.slice(0, 2).toUpperCase();
}

/**
 * Converte string de data civil para Date no fuso horário local do usuário,
 * prevenindo o problema de midnight UTC regredir 1 dia em fusos negativos (GMT-3).
 */
export function parseCivilDate(dateString: string | number | Date): Date {
	if (dateString instanceof Date) return dateString;
	if (typeof dateString === "number") return new Date(dateString);

	const str = String(dateString).trim();
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
	if (match) {
		const [, y, m, d] = match;
		return new Date(Number(y), Number(m) - 1, Number(d));
	}
	return new Date(str);
}

/**
 * Formata data de forma segura com fallback.
 */
export function formatDate(
	dateString?: string | number | Date | null,
	options?: Intl.DateTimeFormatOptions,
	locale: string = DateLocale.PtBR,
): string {
	if (!dateString) return "—";
	try {
		const d = parseCivilDate(dateString);
		if (Number.isNaN(d.getTime())) return String(dateString);
		return d.toLocaleDateString(locale, options);
	} catch {
		return String(dateString);
	}
}

export function toCivilDateString(dateString?: string | number | Date | null): string {
	if (!dateString) return "";
	const d = parseCivilDate(dateString);
	if (Number.isNaN(d.getTime())) return "";
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

export function toCivilDateIso(dateString?: string | null): string | undefined {
	if (!dateString) return undefined;
	const trimmed = dateString.trim();
	if (!trimmed) return undefined;
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
	if (match) {
		const [, y, m, d] = match;
		return `${y}-${m}-${d}T00:00:00.000Z`;
	}
	const d = new Date(trimmed);
	if (Number.isNaN(d.getTime())) return undefined;
	return d.toISOString();
}

/**
 * Retorna uma nova instância de Date no início do dia civil (00:00:00.000) no fuso local.
 * Suporta instâncias de Date, timestamps numéricos ou strings de data (ex: "YYYY-MM-DD", ISO).
 */
export function startOfDay(date: Date | string | number): Date {
	const d = parseCivilDate(date);
	return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/**
 * Verifica se a primeira data ocorre estritamente antes da data limite no nível de dia civil.
 * Retorna false se o limite não for fornecido (null/undefined/vazio).
 *
 * @example
 * isDateBefore(currentDate, medication.startDate)
 */
export function isDateBefore(
	date: Date | string | number,
	boundary?: Date | string | number | null,
): boolean {
	if (!boundary) return false;
	const d = startOfDay(date).getTime();
	const b = startOfDay(boundary).getTime();
	if (Number.isNaN(d) || Number.isNaN(b)) return false;
	return d < b;
}

/**
 * Verifica se a primeira data ocorre estritamente após a data limite no nível de dia civil.
 * Retorna false se o limite não for fornecido (null/undefined/vazio).
 *
 * @example
 * isDateAfter(currentDate, medication.endDate)
 */
export function isDateAfter(
	date: Date | string | number,
	boundary?: Date | string | number | null,
): boolean {
	if (!boundary) return false;
	const d = startOfDay(date).getTime();
	const b = startOfDay(boundary).getTime();
	if (Number.isNaN(d) || Number.isNaN(b)) return false;
	return d > b;
}

/**
 * Verifica se uma data está fora do intervalo ativo [startDate, endDate] no nível de dia civil.
 */
export function isDateOutsideRange(
	date: Date | string | number,
	startDate?: Date | string | number | null,
	endDate?: Date | string | number | null,
): boolean {
	return isDateBefore(date, startDate) || isDateAfter(date, endDate);
}

/**
 * Verifica se duas datas correspondem ao mesmo dia civil no fuso local.
 */
export function isSameDay(
	a?: Date | string | number | null,
	b?: Date | string | number | null,
): boolean {
	if (!a || !b) return false;
	const da = parseCivilDate(a);
	const db = parseCivilDate(b);
	if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
	return (
		da.getFullYear() === db.getFullYear() &&
		da.getMonth() === db.getMonth() &&
		da.getDate() === db.getDate()
	);
}

